import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { captureAnalytics } from '../../../actions/preload';
import './commentIndicator.scss';

// The latest comment on a list row, as `receivers#index` summarizes it (ENG-1962):
// { count, latest: { id, notes_preview, user_name, created_at, migrated } }. The key is
// absent, never null, when the row has no comment the viewer may read, and also when the
// summary lookup failed open, so "no summary" always means "no icon", never an error.
export const CommentSummaryTooltip = ({ summary }) => {
  const { latest } = summary;
  return (
    <div className="comment-indicator-tooltip">
      <div className="comment-indicator-tooltip__text">
        {`Record last commented by ${latest.user_name}`}
      </div>
    </div>
  );
};

CommentSummaryTooltip.propTypes = {
  summary: PropTypes.shape({
    count: PropTypes.number.isRequired,
    latest: PropTypes.shape({
      notes_preview: PropTypes.string,
      user_name: PropTypes.string,
      created_at: PropTypes.string,
      migrated: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};

// The row icon that opens the slide-out viewer on its Comments tab. The pencil is kept
// on purpose: it is the glyph users already call the "Note icon", and a speech bubble in
// this cell already means referral SMS status.
const CommentIndicator = ({ record, fromPage }) => {
  const summary = record.comment_summary;
  if (!(summary?.count > 0) || !summary.latest) return null;

  const { count } = summary;
  return (
    <Tooltip
      title={<CommentSummaryTooltip summary={summary} />}
      placement="topLeft"
      overlayClassName="comment-indicator-tooltip-overlay"
    >
      <button
        type="button"
        className="comment-indicator"
        aria-label={`Open comments (${count})`}
        onClick={(event) => {
          event.stopPropagation();
          // A count and a page only: never the comment text or the author.
          captureAnalytics('record_comments_opened_from_list', { from_page: fromPage, comment_count: count });
          record.openComments?.();
        }}
        // The row's own double-click opens the full-page viewer, and stopping only
        // `click` lets a double-click on the icon reach it.
        onDoubleClick={(event) => event.stopPropagation()}
      >
        <EditOutlined />
      </button>
    </Tooltip>
  );
};

CommentIndicator.propTypes = {
  record: PropTypes.shape({
    comment_summary: PropTypes.shape({
      count: PropTypes.number,
      latest: PropTypes.object,
    }),
    openComments: PropTypes.func,
  }).isRequired,
  fromPage: PropTypes.string.isRequired,
};

export default CommentIndicator;
